import { useState, useRef, RefObject } from 'react';
import { UploadState } from './types';

interface UseImageUploadOptions {
  autoUpload?: boolean;
  showProgress?: boolean;
  autoClose?: boolean;
}

export const useImageUpload = (
  onUpload: (file: File) => Promise<string>, 
  onClose: () => void,
  externalFileInputRef?: RefObject<HTMLInputElement>,
  options: UseImageUploadOptions = {}
) => {
  const { autoUpload = false, showProgress = true, autoClose = true } = options;
  
  const [state, setState] = useState<UploadState>({
    selectedFile: null,
    previewUrl: null,
    uploadProgress: 0,
    uploadStatus: 'idle',
    errorMessage: null
  });

  const internalFileInputRef = useRef<HTMLInputElement>(null);
  
  // Use external ref if provided, otherwise use internal ref
  const fileInputRef = externalFileInputRef || internalFileInputRef;

  // Handle file selection
  const handleFileSelection = async (file: File) => {
    // First update the state with the new file
    setState(prev => ({
      ...prev,
      selectedFile: file,
      previewUrl: URL.createObjectURL(file),
      errorMessage: null,
      uploadStatus: 'uploading',
      uploadProgress: 0
    }));
    
    // Auto upload if enabled
    if (autoUpload && file) {
      try {
        // Start upload immediately
        await handleUpload(file);
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  };

  // Reset the component state
  const resetState = () => {
    if (state.previewUrl) {
      URL.revokeObjectURL(state.previewUrl);
    }
    setState({
      selectedFile: null,
      previewUrl: null,
      uploadProgress: 0,
      uploadStatus: 'idle',
      errorMessage: null
    });
  };

  // Handle modal close
  const handleClose = () => {
    resetState();
    onClose();
  };

  // Remove selected image
  const removeSelectedImage = () => {
    resetState();
  };
  
  // Trigger file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Upload the selected file
  const handleUpload = async (fileToUpload = state.selectedFile) => {
    if (!fileToUpload) return;
    
    try {
      // Ensure we're in uploading state
      setState(prev => ({ 
        ...prev, 
        uploadStatus: 'uploading',
        uploadProgress: 0
      }));
      
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setState(prev => ({
          ...prev,
          uploadProgress: Math.min(prev.uploadProgress + 10, 90) // Cap at 90% until complete
        }));
      }, 300);
      
      // Call the actual upload function provided by parent
      const url = await onUpload(fileToUpload);
      
      // Update to show completion
      clearInterval(progressInterval);
      setState(prev => ({ 
        ...prev, 
        uploadProgress: 100, 
        uploadStatus: 'success' 
      }));
      
      // Only auto-close if enabled (prevents interference with preview step)
      if (autoClose) {
        setTimeout(() => {
          handleClose();
        }, 500);
      }
      
      return url;
    } catch (error) {
      console.error('Upload error:', error);
      setState(prev => ({ 
        ...prev, 
        uploadStatus: 'error', 
        errorMessage: error instanceof Error ? error.message : 'Upload failed. Please try again.'
      }));
      throw error;
    }
  };

  return {
    state,
    handleFileSelection,
    handleClose,
    removeSelectedImage,
    handleUpload,
    triggerFileInput,
    fileInputRef
  };
};
